import { initVersionedSchema } from '@principle-theorem/editor';
import {
  IDestinationEntityJobRunOptions,
  IHasSourceIdentifier,
  ITranslationMap,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  ClinicalNote,
  stafferToNamedDoc,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  type IClinicalNote,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  ISO_DATE_TIME_FORMAT,
  Timezone,
  WithPrimaryKey,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  toISODate,
  toNamedDocument,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import * as he from 'he';
import * as moment from 'moment-timezone';
import { combineLatest, from, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientClinicalNoteDestinationEntity,
  IClinicalNoteJobData,
  IClinicalNoteMigrationData,
} from '../../../destination/entities/patient-clinical-notes';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { getPractitionerOrDefaultMapping } from '../../../mappings/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  PatientSourceEntity,
  PraktikaPatient,
} from '../../source/entities/patient';
import {
  PatientClinicalNoteSourceEntity,
  PraktikaClinicalNote,
  PraktikaClinicalNoteType,
} from '../../source/entities/patient-clinical-notes';
import { PraktikaItemCodeMappingHandler } from '../mappings/item-code';
import { PraktikaStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patients';
import { StafferDestinationEntity } from './staff';

export class PatientClinicalNoteDestinationEntity extends BasePatientClinicalNoteDestinationEntity<PraktikaPatient> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    clinicalNotes: new PatientClinicalNoteSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  customMappings = {
    staff: new PraktikaStafferMappingHandler(),
    itemCodes: new PraktikaItemCodeMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IClinicalNoteJobData<PraktikaPatient>[]> {
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const practitioners$ = from(brand$).pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);

    return snapshotCombineLatest([
      staff$,
      practitioners$,
      sourceItemCodes$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [staff, practitioners, sourceItemCodes]]) =>
        sourcePatients.map((sourcePatient) => ({
          staff,
          practitioners,
          sourcePatient,
          sourceItemCodes,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IClinicalNoteJobData<PraktikaPatient>
  ): Promise<
    | IClinicalNoteMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    const sourcePatientId = data.sourcePatient.data.patient_id.toString();
    const patientRef = await translationMap.getDestination<IPatient>(
      sourcePatientId,
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        `Couldn't resolve patient`
      );
    }

    try {
      const notes = await this.sourceEntities.clinicalNotes.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      );

      const clinicalNotes = await buildClinicalNotes(
        migration,
        notes,
        data.staff,
        [PraktikaClinicalNoteType.Clinical]
      );

      return {
        sourcePatientId,
        patientRef,
        clinicalNotes,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }
}

export async function buildClinicalNotes(
  migration: WithRef<IPracticeMigration>,
  notes: IGetRecordResponse<PraktikaClinicalNote>[],
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[],
  noteTypeFilters: PraktikaClinicalNoteType[] = []
): Promise<(IClinicalNote & IHasSourceIdentifier)[]> {
  return resolveSequentially(
    notes
      .filter((note) => !note.data.date_deleted)
      .filter((note) => {
        if (!noteTypeFilters.length) {
          return true;
        }
        return noteTypeFilters.includes(note.data.type);
      }),
    async (note) => {
      const authorId = note.data.author_id?.toString();
      const sourceOwner = getPractitionerOrDefaultMapping(authorId, staff)
        ?.destinationIdentifier;

      if (!sourceOwner) {
        throw new Error(`No author found for the id ${authorId ?? ''}`);
      }

      const owner = await Firestore.getDoc(sourceOwner);
      const createdAt = moment
        .tz(
          note.data.date_created,
          ISO_DATE_TIME_FORMAT,
          Timezone.AustraliaVictoria
        )
        .tz(migration.configuration.timezone);

      const teethContent = note.data.teeth
        ? `Teeth: ${note.data.teeth}\n\n`
        : '';
      const content = he.decode(
        `${teethContent}${note.data.text ?? ''}`.trim()
      );

      return {
        sourceIdentifier: note.record.uid,
        ...ClinicalNote.init({
          owner: stafferToNamedDoc(owner),
          content: initVersionedSchema(content),
          createdAt: toTimestamp(createdAt),
          immutable: true,
          recordDate: toISODate(createdAt),
        }),
      };
    }
  );
}
